/**
 * ONE tile of the rail's icon row (fix D / R2) — chess.com's shape: a 36 px icon
 * box over an 11 px label, four of them abreast, no borders and no card chrome
 * (R6.1: the rail is one surface).
 *
 * Stateless, so it is a `*.View.tsx` with no model of its own; the four leaves in
 * `tiles/` each bring their own.
 *
 * NO NATIVE TOOLTIP. react-native-web 0.19 does not forward `title` to the DOM
 * (`modules/forwardedProps`), so a disabled tile says WHY in its own label
 * (short enough for the slot) and carries the full sentence as its
 * `accessibilityLabel` — which RNW does forward, as `aria-label`.
 */
import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import Icon, {IconName} from '../../components/Icon';
import {RADIUS, SPACE} from '../../theme/tokens';
import {useV2Theme} from '../../theme/useV2Theme';
import {Txt} from '../../ui';

/** the icon's own box, and the glyph inside it */
const ICON_BOX = 36;
const ICON_SIZE = 20;
const TILE_HEIGHT = 56;
const DISABLED_OPACITY = 0.4;

export type ActionTileProps = {
  icon: IconName;
  label: string;
  /** the full sentence a screen reader reads (and the disabled reason) */
  hint: string;
  onPress?: () => void;
  disabled?: boolean;
  /** `ev` marks a toggle that is ON (the analysis tile) */
  active?: boolean;
  testID?: string;
};

export default React.memo(function ActionTileView({
  icon,
  label,
  hint,
  onPress,
  disabled,
  active,
  testID,
}: ActionTileProps) {
  const tokens = useV2Theme(s => s.tokens);
  const color = active ? tokens.ev : tokens.ink2;

  return (
    <Pressable
      testID={testID}
      disabled={disabled}
      accessibilityLabel={hint}
      onPress={disabled ? undefined : onPress}
      style={({hovered}: any) => [
        styles.tile,
        {
          backgroundColor:
            active || (hovered && !disabled) ? tokens.well : 'transparent',
          opacity: disabled ? DISABLED_OPACITY : 1,
        },
      ]}>
      <View style={styles.iconBox}>
        <Icon name={icon} size={ICON_SIZE} color={color} />
      </View>
      <Txt variant="hint" color={color} numberOfLines={1}>
        {label}
      </Txt>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  tile: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    height: TILE_HEIGHT,
    borderRadius: RADIUS.control,
    paddingHorizontal: SPACE.xs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBox: {
    width: ICON_BOX,
    height: ICON_BOX,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
